"use client";
/**
 * SANATAN DECODE — v14 "TREE". 21s @ 30fps, 1280x720.
 *
 * The other explainer engine we had never used: a DIAGRAM that builds while the voice talks,
 * instead of props that arrive next to the voice. Johnny Harris's maps and Vox's charts do
 * the arguing; the narration only names what you are already watching.
 *
 * Here the whole film is ONE object — a descent tree — obeying LAW 3 harder than any previous
 * version. It draws generation by generation across the रक्त-रेखा beat, shrinks and slides
 * left for the शास्त्र beat where two distant leaves light up and trace back to the same root
 * (that IS the sagotra argument, drawn rather than asserted), and finally its two lit leaves
 * BECOME the two carrier circles of the science beat. One object, three meanings — the same
 * graphic-match law the marigold thread follows in v10, applied to the film's spine.
 *
 * Almost no assets: the tree is SVG. That also makes this the cheapest version to make new
 * episodes from — a different topic needs a different diagram, not a different asset set.
 */
import React from "react";
import { AbsoluteFill, Audio, staticFile, interpolate, Easing } from "remotion";
import {
  INK, GOLD, MARIGOLD, KUMKUM, MUTED_C, deva, inter,
  f, useStep, useKeys, useSpringAt, W,
  Pano, Grain, Petals, Word, Row, Line, Beat, useCamera, Prop, Disc, Furniture,
} from "./sanatan/kit";

/* ══ TREE GEOMETRY ═════════════════════════════════════════════
   Five generations, 1 → 2 → 4 → 8 → 16. Positions are pure functions of (gen, index) so the
   same node can be asked for at any scale, in any beat, without storing state. */
const GENS = 5;
const GEN_X = [188, 370, 552, 734, 916];
const Y_TOP = 128, Y_BOT = 604;
const nodeY = (g: number, i: number) => {
  const n = 2 ** g;
  return Y_TOP + ((Y_BOT - Y_TOP) * (i + 1)) / (n + 1);
};
const COUNTS = [1, 2, 4, 8, 16];
/** when each generation finishes drawing */
const GEN_AT = [6.30, 6.72, 7.10, 7.48, 7.86];

/** the two distant descendants the शास्त्र beat puts on trial */
const LEAF_A = 2, LEAF_B = 13;
/** every ancestor index of a leaf, walking back up: gen g index = leaf >> (4-g) */
const ancestry = (leaf: number) => Array.from({ length: GENS }, (_, g) => leaf >> (GENS - 1 - g));
const PATH_A = ancestry(LEAF_A), PATH_B = ancestry(LEAF_B);
const onPath = (g: number, i: number) => PATH_A[g] === i || PATH_B[g] === i;

/* ══ THE PERSISTENT TREE ═══════════════════════════════════════
   Lives at stage level from 6.2 to the end. It is NEVER unmounted and re-created — it moves,
   rescales, changes what it emphasises, and finally hands its two lit leaves to beat F.     */
const PersistentTree: React.FC = () => {
  const frame = useStep();
  const born = useSpringAt(6.2, 10, 90, 1.2);

  // the pose: full frame through beat C, then shifted left + shrunk for the शास्त्र beat,
  // then pushed further back while the science beat takes the foreground.
  const sc = useKeys([[6.2, 1], [10.3, 1], [11.0, 0.74], [15.5, 0.74], [16.1, 0.52], [21.2, 0.52]]);
  const tx = useKeys([[6.2, 0], [10.3, 0], [11.0, -96], [15.5, -96], [16.1, -250], [21.2, -250]]);
  const ty = useKeys([[6.2, 0], [10.3, 0], [11.0, 34], [15.5, 34], [16.1, 96], [21.2, 96]]);
  const dim = useKeys([[15.5, 1], [16.3, 0.30]]);          // recedes, never disappears
  const trial = useKeys([[10.9, 0], [11.7, 1]]);           // the two paths light up
  const conv = useKeys([[13.2, 0], [14.2, 1]]);            // …and converge on the one root

  if (frame < f(6.15)) return null;

  const drawn = (g: number) =>
    interpolate(frame, [f(GEN_AT[g] - 0.42), f(GEN_AT[g])], [0, 1], {
      extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
    });

  const edges: React.ReactNode[] = [];
  const nodes: React.ReactNode[] = [];

  for (let g = 0; g < GENS; g++) {
    const n = 2 ** g;
    for (let i = 0; i < n; i++) {
      const x = GEN_X[g], y = nodeY(g, i);
      const lit = onPath(g, i) ? trial : 0;
      const born_g = drawn(g);
      if (born_g <= 0) continue;

      if (g > 0) {
        const px = GEN_X[g - 1], py = nodeY(g - 1, i >> 1);
        const ex = px + (x - px) * born_g, ey = py + (y - py) * born_g;
        // the two paths on trial thicken and go kumkum; everything else stays structure
        edges.push(
          <path key={`e${g}-${i}`}
            d={`M${px} ${py} C ${(px + x) / 2} ${py}, ${(px + x) / 2} ${ey}, ${ex} ${ey}`}
            fill="none"
            stroke={lit > 0.02 ? KUMKUM : MUTED_C}
            strokeWidth={(lit > 0.02 ? 2.2 + 2.6 * lit : 1.6) * (1 + 0.5 * conv * lit)}
            opacity={(lit > 0.02 ? 0.35 + 0.65 * lit : 0.42) * born_g}
            strokeLinecap="round" />
        );
      }
      const r = (g === 0 ? 13 : g === GENS - 1 ? 6 : 8) * (0.4 + 0.6 * born_g);
      nodes.push(
        <circle key={`n${g}-${i}`} cx={x} cy={y} r={r + 2.4 * lit}
          fill={g === 0 ? MARIGOLD : lit > 0.02 ? KUMKUM : "#FBF3E2"}
          stroke={g === 0 ? GOLD : lit > 0.02 ? KUMKUM : MUTED_C}
          strokeWidth={g === 0 ? 3.4 : 2}
          opacity={born_g} />
      );
    }
  }

  return (
    <div style={{
      position: "absolute", inset: 0, opacity: Math.min(1, born * 1.6) * dim,
      transform: `translate(${tx}px, ${ty}px) scale(${sc})`, transformOrigin: "50% 46%",
    }}>
      <svg width={1280} height={720} style={{ position: "absolute", overflow: "visible" }}>
        {edges}
        {nodes}
        {/* the root's name, and the count of each generation — the only numbers on screen */}
        {COUNTS.map((c, g) => {
          const a = interpolate(frame, [f(GEN_AT[g]), f(GEN_AT[g] + 0.3)], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          if (a <= 0) return null;
          return (
            <text key={`c${g}`} x={GEN_X[g]} y={Y_TOP - 34} textAnchor="middle"
              fontFamily={inter} fontSize={g === 0 ? 30 : 26} fontWeight={800}
              fill={g === 0 ? MARIGOLD : MUTED_C} opacity={a * 0.9}>{c}</text>
          );
        })}
      </svg>
    </div>
  );
};

/* ══ BEATS ═════════════════════════════════════════════════════ */
/** A ruled form line — the claim "गोत्र is not your surname" is easiest to see as the wrong
    box on a form, so the anchor is a document, not a prop. */
const BeatA: React.FC = () => {
  const rule = useKeys([[0.1, 0], [1.1, 1]]);
  const cross = useKeys([[2.9, 0], [3.35, 1]]);
  return (
    <>
      <Disc at={0.0} out={3.6} x={880} y={300} r={250} color={MARIGOLD} />
      <Prop src="sanatan/manuscript.png" w={560} left={392} top={430} inAt={-0.4} outAt={3.55} from="right" amp={10} travel={110} />
      <div style={{ position: "absolute", left: 0, right: 0, top: 118 }}>
        <Row gap={14}>{[0, 1, 2, 3, 4, 5, 6].map((i) => <Word key={i} idx={i} size={38} color={MUTED_C} weight={600} underline={i === 2} outAt={3.6} />)}</Row>
        <div style={{ height: 14 }} />
        <Row>
          <Word idx={8} size={74} weight={900} outAt={3.62} />
          <Word idx={9} size={74} weight={900} outAt={3.64} />
          <Word idx={10} size={74} weight={900} color={KUMKUM} strike outAt={3.66} />
          <Word idx={11} size={74} weight={900} outAt={3.68} />
          <Word idx={12} size={74} weight={900} outAt={3.7} />
        </Row>
      </div>
      {/* the form: a ruled field being filled in, then crossed out on "सरनेम" */}
      <svg width={1280} height={720} style={{ position: "absolute", pointerEvents: "none" }}>
        <line x1={392} y1={366} x2={392 + 496 * rule} y2={366} stroke={INK} strokeWidth={3} opacity={0.42} />
        <line x1={392} y1={366} x2={392 + 496 * cross} y2={366 - 0} stroke={KUMKUM} strokeWidth={4} opacity={cross * 0.9} />
      </svg>
    </>
  );
};

const BeatB: React.FC = () => {
  const halo = useSpringAt(4.35, 11, 80);
  const gone = useSpringAt(5.5, 200, 70);
  const pulse = useStep();
  return (
    <>
      <Disc at={3.9} out={5.5} x={640} y={300} r={280} color={GOLD} op={0.18} />
      {/* the seed of the tree: one node, alone, already in the position the root will take */}
      <svg width={1280} height={720} style={{ position: "absolute", opacity: halo * (1 - gone) }}>
        <circle cx={640} cy={300} r={128 + halo * 34 + Math.sin(pulse / 20) * 5} fill="none" stroke={GOLD} strokeWidth={2} opacity={0.55} />
        <circle cx={640} cy={300} r={96 + halo * 22} fill="none" stroke={MARIGOLD} strokeWidth={1.5} opacity={0.55} />
        <circle cx={640} cy={300} r={15 + halo * 4} fill={MARIGOLD} stroke={GOLD} strokeWidth={4} />
      </svg>
      <div style={{ position: "absolute", left: 0, right: 0, top: 470, textAlign: "center" }}>
        <Row><Word idx={15} size={78} weight={900} color={KUMKUM} underline outAt={5.52} /></Row>
      </div>
    </>
  );
};

/** The tree itself is at stage level; this beat only labels it. */
const BeatC: React.FC = () => (
  <>
    <div style={{ position: "absolute", left: 0, right: 0, top: 622, textAlign: "center" }}>
      <Row gap={14}>
        <Word idx={26} size={44} weight={800} outAt={10.2} />
        <Word idx={27} size={44} weight={800} outAt={10.22} />
        <Word idx={28} size={56} weight={900} color={KUMKUM} underline outAt={10.24} />
      </Row>
    </div>
    <div style={{ position: "absolute", left: 96, top: 640 }}>
      <Line text="एक मूल" at={6.45} outAt={10.2} size={22} color={MARIGOLD} weight={800} />
    </div>
    <div style={{ position: "absolute", left: 856, top: 640 }}>
      <Line text="आज की पीढ़ी" at={7.95} outAt={10.2} size={22} color={INK} weight={800} />
    </div>
  </>
);

/** The prohibition, drawn: two distant descendants, one root. The ✕ is placed exactly between
    the two lit leaves, so the rule is read off the diagram rather than asserted over it. */
const BeatDE: React.FC = () => {
  const tag = useSpringAt(11.61, 10, 150);
  const tagOut = useSpringAt(15.4, 200, 80);
  const x = useSpringAt(13.41, 9, 130);
  const xOut = useSpringAt(15.42, 200, 80);
  const ax = GEN_X[GENS - 1] * 0.74 - 96 + 640 * (1 - 0.74);
  const ay = (y: number) => y * 0.74 + 34 + 331 * (1 - 0.74);
  const yA = ay(nodeY(GENS - 1, LEAF_A)), yB = ay(nodeY(GENS - 1, LEAF_B));
  return (
    <>
      <Disc at={10.6} out={15.4} x={880} y={300} r={280} color={MARIGOLD} op={0.12} />
      <svg width={1280} height={720} style={{ position: "absolute", pointerEvents: "none" }}>
        {/* the forbidden join: a line between the two lit leaves, struck through */}
        <line x1={ax} y1={yA} x2={ax} y2={yB} stroke={KUMKUM} strokeWidth={3}
          strokeDasharray="7 7" opacity={0.55 * x * (1 - xOut)} />
        <g opacity={x * (1 - xOut)} transform={`translate(${ax + 66},${(yA + yB) / 2}) scale(${0.7 + 0.3 * x}) rotate(${-18 * (1 - x)})`}>
          <line x1={-26} y1={-26} x2={26} y2={26} stroke={KUMKUM} strokeWidth={9} strokeLinecap="round" />
          <line x1={26} y1={-26} x2={-26} y2={26} stroke={KUMKUM} strokeWidth={9} strokeLinecap="round" />
        </g>
      </svg>
      <div style={{ position: "absolute", right: 74, top: 168, width: 430, textAlign: "right" }}>
        <div style={{
          display: "inline-block", fontFamily: deva, fontSize: 22, fontWeight: 800, wordSpacing: 8,
          color: "#F7EFDD", background: INK, padding: "6px 26px", borderRadius: 3,
          opacity: Math.min(1, tag * 1.5) * (1 - tagOut),
          transform: `translateY(${(1 - tag) * 18}px) rotate(${5 * (1 - tag)}deg)`,
        }}>{W[34][2]}</div>
        <div style={{ height: 20 }} />
        <div style={{ textAlign: "right" }}>
          <Row gap={16}>
            <Word idx={38} size={64} weight={900} outAt={15.4} />
            <Word idx={39} size={64} weight={900} outAt={15.42} />
          </Row>
          <Row><Word idx={40} size={72} weight={900} color={KUMKUM} outAt={15.44} /></Row>
        </div>
      </div>
    </>
  );
};

/** The two lit leaves ARE these two carriers — same colour, same radius family, arriving at
    the moment the tree recedes. The diagram never restarts, it hands over. */
const BeatF: React.FC = () => {
  const frame = useStep();
  const CX = 780, PY = 236, CY = 404;
  const pA = useSpringAt(15.9, 9, 140);
  const pB = useSpringAt(16.2, 9, 140);
  const kid = useSpringAt(17.15, 8, 150);
  const term = useSpringAt(17.43, 10, 120);
  const bob = Math.sin(frame / 24) * 5;
  const Allele: React.FC<{ x: number; fill: string; opacity?: number }> = ({ x, fill, opacity = 1 }) => (
    <rect x={x - 7} y={-20} width={14} height={40} rx={7} fill={fill} opacity={opacity} />
  );
  const Carrier: React.FC<{ x: number; s: number; ph: number }> = ({ x, s, ph }) => (
    <g opacity={s} transform={`translate(${x},${PY + Math.sin((frame + ph) / 26) * 6}) scale(${0.8 + s * 0.2}) rotate(${8 * (1 - s)})`}>
      <circle r={44} fill="#FDF8EC" stroke={KUMKUM} strokeWidth={4} />
      <Allele x={-15} fill={INK} opacity={0.22} />
      <Allele x={15} fill={KUMKUM} />
    </g>
  );
  return (
    <>
      <Disc at={15.8} out={22} x={880} y={330} r={260} color={INK} op={0.08} />
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 140} s={pA} ph={0} />
        <Carrier x={CX + 140} s={pB} ph={40} />
        <line x1={CX - 140} y1={PY + 52} x2={CX} y2={CY - 54} stroke={MUTED_C} strokeWidth={3} opacity={kid * 0.6} />
        <line x1={CX + 140} y1={PY + 52} x2={CX} y2={CY - 54} stroke={MUTED_C} strokeWidth={3} opacity={kid * 0.6} />
        <g opacity={kid} transform={`translate(${CX},${CY + bob}) scale(${0.78 + kid * 0.22}) rotate(${-10 * (1 - kid)})`}>
          <circle r={46} fill={KUMKUM} opacity={0.14} />
          <circle r={46} fill="none" stroke={KUMKUM} strokeWidth={5} />
          <Allele x={-15} fill={KUMKUM} />
          <Allele x={15} fill={KUMKUM} />
        </g>
      </svg>
      <div style={{
        position: "absolute", left: 62, top: 470, width: 560, textAlign: "left",
        opacity: Math.min(1, term * 1.4),
        transform: `translateY(${(1 - term) * 34}px) rotate(${3 * (1 - term)}deg)`,
      }}>
        <div style={{ fontFamily: inter, fontSize: 40, fontWeight: 800, letterSpacing: 5, color: KUMKUM }}>CONSANGUINITY</div>
        <div style={{ height: 8 }} />
        <div style={{ fontFamily: deva, fontSize: 25, fontWeight: 700, color: INK, opacity: 0.82 }}>विज्ञान ने इसे एक नाम दिया।</div>
      </div>
    </>
  );
};

export const SanatanDecodeGotraV14: React.FC = () => {
  const cam = useCamera();
  return (
    <AbsoluteFill style={{ backgroundColor: "#F4EAD2", overflow: "hidden" }}>
      <Audio src={staticFile("sanatan/gotra.mp3")} />
      {/* the ground is washed much further back than usual — a diagram needs a quiet page */}
      <Pano blur={6.5} sat={0.42} bright={1.16} contrast={0.76} wash={0.56} />
      <AbsoluteFill style={{ transform: cam }}>
        <Beat from={0} to={3.75}><BeatA /></Beat>
        <Beat from={3.9} to={5.6}><BeatB /></Beat>
        <Beat from={5.8} to={10.4}><BeatC /></Beat>
        <Beat from={10.6} to={15.6}><BeatDE /></Beat>
        <Beat from={15.8} to={21.1}><BeatF /></Beat>
        {/* the film's spine: one diagram, never re-created */}
        <PersistentTree />
      </AbsoluteFill>
      <Petals n={9} op={0.16} />
      <Grain op={0.11} />
      <Furniture />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
